import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { getProductPricing, createKit, createKitBulk, updateKit, updateKitBulk, getKitDetail, getBatchWarehouses, getCategories, getWarehouses, getKitsList } from '../config/api';
import { toast } from 'react-toastify';
import useIsMobile from '../hooks/useIsMobile';

// Compact inline dropdown for warehouse/kit switching
const SwitcherDropdown = ({ label, value, options, onChange, disabled }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selected = options.find((o) => o.value === value);

  return (
    <div ref={ref} style={{ position: 'relative', minWidth: '160px', maxWidth: '240px' }}>
      <button
        type="button"
        onClick={() => !disabled && setOpen((p) => !p)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '6px',
          padding: '5px 10px',
          fontSize: '12px',
          background: 'white',
          border: '1px solid #dee2e6',
          borderRadius: '6px',
          cursor: disabled ? 'default' : 'pointer',
          color: '#343a40',
          fontWeight: 500,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
          <span style={{ color: '#6c757d', fontWeight: 400, marginRight: '4px' }}>{label}:</span>
          {disabled && options.length === 0 ? 'Loading…' : (selected?.label || '—')}
        </span>
        <span style={{ fontSize: '10px', color: '#6c757d', flexShrink: 0 }}>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 4px)',
          left: 0,
          zIndex: 1000,
          background: 'white',
          border: '1px solid #dee2e6',
          borderRadius: '6px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          minWidth: '100%',
          maxHeight: '240px',
          overflowY: 'auto',
        }}>
          {options.map((opt) => (
            <div
              key={opt.value}
              onClick={() => { onChange(opt.value); setOpen(false); }}
              style={{
                padding: '8px 12px',
                fontSize: '12px',
                cursor: 'pointer',
                background: opt.value === value ? '#f0f7ff' : 'white',
                color: opt.value === value ? '#0d6efd' : '#343a40',
                fontWeight: opt.value === value ? 600 : 400,
                borderBottom: '1px solid #f0f0f0',
                whiteSpace: 'nowrap',
              }}
              onMouseOver={(e) => { if (opt.value !== value) e.currentTarget.style.background = '#f8f9fa'; }}
              onMouseOut={(e) => { if (opt.value !== value) e.currentTarget.style.background = 'white'; }}
            >
              {opt.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const CreateKit = ({ isEdit = false }) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { kitId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const fromWarehouseParam = searchParams.get('fromWarehouse');
  const rawWarehouseId = searchParams.get('warehouseId') || 'all';
  const isFromAllWarehouses = isEdit && (fromWarehouseParam === 'all' || rawWarehouseId === 'all');
  const [currentWarehouseId, setCurrentWarehouseId] = useState(
    isEdit ? (isFromAllWarehouses ? 'all' : (rawWarehouseId !== 'all' ? rawWarehouseId : '1')) : rawWarehouseId
  );

  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(isEdit);
  const [categories, setCategories] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  // Bulk Warehouse Selection Modal States
  const [showWarehouseModal, setShowWarehouseModal] = useState(false);
  const [allWarehousesList, setAllWarehousesList] = useState([]);
  const [warehousesLoading, setWarehousesLoading] = useState(false);
  const [selectedWarehouseIds, setSelectedWarehouseIds] = useState([]);
  const [pendingPayload, setPendingPayload] = useState(null);
  const [kitBatchId, setKitBatchId] = useState(null);
  const [batchWarehouseIds, setBatchWarehouseIds] = useState([]);
  // Switcher: all warehouses this kit batch is deployed to
  const [batchWarehouses, setBatchWarehouses] = useState([]);
  const [batchSwitcherLoading, setBatchSwitcherLoading] = useState(false);
  // Switcher: selected warehouse id (drives the kit list)
  const [selectedWarehouseId, setSelectedWarehouseId] = useState(null);
  // Switcher: all kits in the selected warehouse
  const [warehouseKits, setWarehouseKits] = useState([]);
  const [kitSwitcherLoading, setKitSwitcherLoading] = useState(false);

  const [kitData, setKitData] = useState({
    name: '',
    description: '',
    discount_percent: '',
    gst_percent: '',
    hsn_code: '',
    min_optional_count: 0,
    tags: '',
  });

  const [kitCategories, setKitCategories] = useState([]);
  const [skuSearch, setSkuSearch] = useState({});
  const [collapsedCategories, setCollapsedCategories] = useState({});

  // Hardcoded panel variants — admin assigns panel SKUs to each
  const PANEL_VARIANTS = [
    { id: 'bifacial_dcr', label: 'Bifacial DCR' },
    { id: 'topcon_dcr', label: 'TopCon DCR' },
  ];
  const PANEL_CATEGORY_ID = 1;
  // panelVariantProducts: { bifacial_dcr: [productId,...], topcon_dcr: [productId,...] }
  const [panelVariantProducts, setPanelVariantProducts] = useState({ bifacial_dcr: [], topcon_dcr: [] });
  const [variantSearch, setVariantSearch] = useState({});

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('userData_30jan') || '{}');
    if (userData.role !== 'superAdmin' && !userData.access?.includes('marketing')) {
      window.location.href = '/orders';
      return;
    }
    loadCategories();
    if (isEdit && kitId) {
      // Reset switcher state so stale data from the previous kit doesn't flash
      setBatchWarehouses([]);
      setWarehouseKits([]);
      setSelectedWarehouseId(null);
      setPageLoading(true);
      loadKitForEdit();
    } else {
      loadProducts(currentWarehouseId);
    }
  }, [kitId]);

  const loadKitForEdit = async () => {
    try {
      const response = await getKitDetail(kitId);
      if (response && response.data && response.data.kit) {
        const kit = response.data.kit;
        const bId = kit.kit_batch_id || kit.batch_id || kit.batchId || null;
        if (bId) {
          setKitBatchId(bId);
          setBatchSwitcherLoading(true);
          getBatchWarehouses(bId).then((res) => {
            if (res && res.data && Array.isArray(res.data.warehouses)) {
              setBatchWarehouses(res.data.warehouses);
            }
          }).catch(() => {}).finally(() => setBatchSwitcherLoading(false));
        }
        // Set selected warehouse and load its kits for the kit switcher
        const whId = kit.warehouse_id || null;
        setSelectedWarehouseId(whId);
        if (whId) {
          setKitSwitcherLoading(true);
          getKitsList(whId).then((res) => {
            if (res && res.data && Array.isArray(res.data.kits)) {
              setWarehouseKits(res.data.kits);
            }
          }).catch(() => {}).finally(() => setKitSwitcherLoading(false));
        }

        const actualWhId = isFromAllWarehouses
          ? 'all'
          : (kit.warehouse_id ? String(kit.warehouse_id) : (currentWarehouseId !== 'all' ? currentWarehouseId : '1'));

        setCurrentWarehouseId(actualWhId);

        // Fetch products: if editing from All Warehouses, actualWhId is 'all' (getAllProducts?warehouseId=all)
        await loadProducts(actualWhId);

        setKitData({
          name: kit.name || '',
          description: kit.description || '',
          discount_percent: kit.discount_percent || '',
          gst_percent: kit.gst_percent || '',
          hsn_code: kit.hsn_code || '',
          min_optional_count: kit.min_optional_count || 0,
          tags: kit.tags || '',
        });
        if (kit.image) setImagePreview(kit.image);
        // Map categories
        const mappedCategories = (kit.categories || []).map((cat) => ({
          category_id: String(cat.category_id),
          is_mandatory: cat.is_mandatory,
          min_qty: cat.min_qty,
          max_qty: cat.max_qty,
          sort_order: cat.sort_order,
          label: cat.label || '',
          skus: (cat.skus || []).map((sku) => ({
            product_id: sku.product_id,
            product_name: sku.product ? sku.product.name : `Product #${sku.product_id}`,
            sku_code: sku.product ? sku.product.skuCode : '',
          })),
        }));
        setKitCategories(mappedCategories);

        // Load panel variant product assignments from stored variants
        if (kit.variants && kit.variants.length > 0) {
          const pvp = { bifacial_dcr: [], topcon_dcr: [] };
          kit.variants.forEach((v) => {
            if (pvp[v.id] !== undefined && v.products?.[String(PANEL_CATEGORY_ID)]) {
              pvp[v.id] = v.products[String(PANEL_CATEGORY_ID)];
            }
          });
          setPanelVariantProducts(pvp);
        }
      }
    } catch (error) {
      toast.error('Failed to load kit data');
    } finally {
      setPageLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await getCategories();
      if (response && response.data) {
        setCategories(response.data);
      }
    } catch (error) {
      toast.error('Failed to load categories');
    }
  };

  const loadProducts = async (whId = currentWarehouseId) => {
    setProductsLoading(true);
    try {
      const response = await getProductPricing(whId || 'all');
      if (response && response.data) {
        setAllProducts(response.data);
      }
    } catch (error) {
      toast.error('Failed to load products');
    } finally {
      setProductsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setKitData({ ...kitData, [name]: value });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Image must be less than 10MB');
        return;
      }
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  // Category management
  const addCategory = () => {
    setKitCategories([
      ...kitCategories,
      {
        category_id: '',
        is_mandatory: true,
        min_qty: 1,
        max_qty: 1,
        sort_order: kitCategories.length,
        label: '',
        skus: [],
      },
    ]);
  };

  const removeCategory = (index) => {
    setKitCategories(kitCategories.filter((_, i) => i !== index));
  };

  const updateCategory = (index, field, value) => {
    const updated = [...kitCategories];
    updated[index] = { ...updated[index], [field]: value };
    if (field === 'category_id') {
      updated[index].skus = [];
    }
    setKitCategories(updated);
  };

  // SKU management
  const addSku = (catIndex, productId) => {
    const updated = [...kitCategories];
    const product = allProducts.find((p) => p.pid === parseInt(productId));
    if (!product) return;
    if (updated[catIndex].skus.find((s) => s.product_id === parseInt(productId))) {
      toast.warning('This product is already added');
      return;
    }
    updated[catIndex].skus.push({
      product_id: parseInt(productId),
      product_name: product.productName,
      sku_code: product.skuId,
    });
    setKitCategories(updated);
  };

  const removeSku = (catIndex, skuIndex) => {
    const updated = [...kitCategories];
    updated[catIndex].skus = updated[catIndex].skus.filter((_, i) => i !== skuIndex);
    setKitCategories(updated);
  };

  const selectAllSkus = (catIndex) => {
    const updated = [...kitCategories];
    const cat = updated[catIndex];
    if (!cat || !cat.category_id) return;

    // Get available products for this row (excludes products assigned in other rows of the same category)
    let candidateProducts = getAvailableProductsForCategory(catIndex);

    // If search term is active for this row, filter candidates by search term
    const search = (skuSearch[catIndex] || '').toLowerCase().trim();
    if (search) {
      candidateProducts = candidateProducts.filter(
        (p) =>
          p.productName.toLowerCase().includes(search) ||
          (p.skuId || '').toLowerCase().includes(search)
      );
    }

    const existingIds = new Set(cat.skus.map((s) => s.product_id));
    const newSkus = candidateProducts
      .filter((p) => !existingIds.has(p.pid))
      .map((p) => ({
        product_id: p.pid,
        product_name: p.productName,
        sku_code: p.skuId,
      }));

    if (newSkus.length > 0) {
      updated[catIndex] = {
        ...cat,
        skus: [...cat.skus, ...newSkus],
      };
      setKitCategories(updated);
    }
  };

  const getAvailableProductsForCategory = (catIndex) => {
    const cat = kitCategories[catIndex];
    if (!cat || !cat.category_id) return [];

    // Products already assigned in other rows of the same category (not this row)
    const usedProductIds = new Set(
      kitCategories
        .filter((c, i) => i !== catIndex && String(c.category_id) === String(cat.category_id))
        .flatMap((c) => c.skus.map((s) => s.product_id))
    );

    return allProducts.filter(
      (p) => p.category_id === parseInt(cat.category_id) && !usedProductIds.has(p.pid)
    );
  };

  // Solar Panel and Inverter can only be added once; BOS and others can repeat
  const SINGLE_INSTANCE_CATEGORIES = [1, 2];

  const getUsedCategoryIds = () => {
    return kitCategories.map((c) => parseInt(c.category_id)).filter(Boolean);
  };

  const toggleCategoryCollapse = (index) => {
    setCollapsedCategories((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const togglePanelVariantProduct = (variantId, productId) => {
    setPanelVariantProducts((prev) => {
      const current = prev[variantId] ? [...prev[variantId]] : [];
      const idx = current.indexOf(productId);
      if (idx >= 0) {
        current.splice(idx, 1);
      } else {
        current.push(productId);
      }
      return { ...prev, [variantId]: current };
    });
  };

  const selectAllPanelVariantProducts = (variantId, targetSkus = null) => {
    const panelCat = kitCategories.find((c) => parseInt(c.category_id) === PANEL_CATEGORY_ID);
    if (!panelCat) return;
    const skusToSelect = targetSkus || panelCat.skus;
    const idsToSelect = skusToSelect.map((s) => s.product_id);
    setPanelVariantProducts((prev) => {
      const current = prev[variantId] || [];
      const combined = Array.from(new Set([...current, ...idsToSelect]));
      return { ...prev, [variantId]: combined };
    });
  };

  const clearPanelVariantProducts = (variantId, targetSkus = null) => {
    setPanelVariantProducts((prev) => {
      if (!targetSkus) {
        return { ...prev, [variantId]: [] };
      }
      const idsToRemove = targetSkus.map((s) => s.product_id);
    });
  };

  const fetchWarehousesForModal = async () => {
    setWarehousesLoading(true);
    try {
      const res = await getWarehouses();
      let fullList = [];
      if (res && res.data) {
        fullList = res.data;
        setAllWarehousesList(fullList);
      }

      // If kit has a batch_id, fetch deployed warehouses for this batch
      if (kitBatchId) {
        try {
          const batchRes = await getBatchWarehouses(kitBatchId);
          if (batchRes && batchRes.data && Array.isArray(batchRes.data.warehouses) && batchRes.data.warehouses.length > 0) {
            const deployedIds = batchRes.data.warehouses.map((w) => Number(w.warehouseId || w.warehouse_id));
            setBatchWarehouseIds(deployedIds);
            setSelectedWarehouseIds(deployedIds);
            return;
          }
        } catch (batchErr) {
          console.warn('Could not fetch batch warehouses:', batchErr);
        }
      }

      // Default pre-selection: select all active warehouses if no batch warehouses returned
      setSelectedWarehouseIds(fullList.map((w) => w.id));
    } catch (err) {
      toast.error('Failed to load warehouses list');
    } finally {
      setWarehousesLoading(false);
    }
  };

  const selectableWarehouses = allWarehousesList;

  // Warehouse switcher handler — fetch kits for selected warehouse, auto-navigate to first kit
  const handleWarehouseSwitch = async (warehouseId) => {
    if (!warehouseId || warehouseId === selectedWarehouseId) return;
    setSelectedWarehouseId(warehouseId);
    setWarehouseKits([]);
    setKitSwitcherLoading(true);
    try {
      const res = await getKitsList(warehouseId);
      const kits = (res && res.data && Array.isArray(res.data.kits)) ? res.data.kits : [];
      setWarehouseKits(kits);
      // Navigate to the first kit of the selected warehouse
      if (kits.length > 0) {
        navigate(`/kits/edit/${kits[0].id}`);
      }
    } catch {
      // silently fail — kit list stays empty
    } finally {
      setKitSwitcherLoading(false);
    }
  };

  const handleToggleSelectAllWarehouses = (e) => {
    if (e.target.checked) {
      setSelectedWarehouseIds(allWarehousesList.map((w) => w.id));
    } else {
      setSelectedWarehouseIds([]);
    }
  };

  const handleToggleSingleWarehouse = (whId) => {
    setSelectedWarehouseIds((prev) =>
      prev.includes(whId) ? prev.filter((id) => id !== whId) : [...prev, whId]
    );
  };

  const handleBulkUpdateSubmit = async () => {
    if (!selectedWarehouseIds || selectedWarehouseIds.length === 0) {
      toast.error('Please select at least one warehouse to apply changes');
      return;
    }
    setShowWarehouseModal(false);
    setLoading(true);
    try {
      const bulkPayload = {
        ...pendingPayload,
        warehouse_ids: selectedWarehouseIds,
      };
      const targetBatchId = kitBatchId || kitId;
      await updateKitBulk(targetBatchId, bulkPayload, imageFile);
      toast.success(`Kit updated for ${selectedWarehouseIds.length} warehouses successfully!`);
      navigate('/kits');
    } catch (error) {
      toast.error(error.message || 'Failed to update kit for selected warehouses');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!kitData.name.trim()) {
      toast.error('Kit name is required');
      return;
    }
    if (!kitData.description.trim()) {
      toast.error('Description is required');
      return;
    }
    if (!kitData.hsn_code || !kitData.hsn_code.trim()) {
      toast.error('HSN Code is required');
      return;
    }
    if (kitData.hsn_code.trim().length < 6) {
      toast.error('HSN Code must be at least 6 digits');
      return;
    }
    if (kitData.discount_percent === '' || kitData.discount_percent === null || kitData.discount_percent === undefined) {
      toast.error('Kit Discount % is required');
      return;
    }
    if (!kitData.gst_percent) {
      toast.error('GST % is required');
      return;
    }
    if (!kitData.tags || !kitData.tags.trim()) {
      toast.error('Tags are required');
      return;
    }
    if (!imageFile && !imagePreview) {
      toast.error('Kit image is required');
      return;
    }

    // Validate min_optional_count against actual optional categories
    const optionalCatCount = kitCategories.filter((c) => !c.is_mandatory).length;
    const minOptional = parseInt(kitData.min_optional_count) || 0;
    if (minOptional > optionalCatCount) {
      toast.error(`Min Optional Categories (${minOptional}) cannot exceed the number of optional categories added (${optionalCatCount}). Either reduce the value or mark some categories as Optional.`);
      return;
    }
    if (kitCategories.length === 0) {
      toast.error('Add at least one category');
      return;
    }
    for (let i = 0; i < kitCategories.length; i++) {
      const cat = kitCategories[i];
      if (!cat.category_id) {
        toast.error(`Select a category for row ${i + 1}`);
        return;
      }
      if (parseInt(cat.category_id) === 3 && (!cat.label || !cat.label.trim())) {
        toast.error(`Label is required for BOS category in row ${i + 1}`);
        return;
      }
      if (cat.skus.length === 0) {
        toast.error(`Add at least one eligible SKU for category row ${i + 1}`);
        return;
      }
    }

    // If panel category is present, validate variant product assignments
    const panelCat = kitCategories.find((c) => parseInt(c.category_id) === PANEL_CATEGORY_ID);
    if (panelCat && panelCat.skus.length > 0) {
      const totalAssigned = PANEL_VARIANTS.reduce((sum, v) => sum + (panelVariantProducts[v.id]?.length || 0), 0);
      if (totalAssigned === 0) {
        toast.error('Please assign panel SKUs to Bifacial DCR or TopCon DCR variants');
        return;
      }
    }

    const payload = {
      name: kitData.name.trim(),
      category_id: 4,
      description: kitData.description.trim() || null,
      discount_percent: parseFloat(kitData.discount_percent) || 0,
      gst_percent: parseFloat(kitData.gst_percent) || null,
      hsn_code: kitData.hsn_code || null,
      tags: kitData.tags.trim() || null,
      min_optional_count: parseInt(kitData.min_optional_count) || 0,
      warehouse_id: currentWarehouseId && currentWarehouseId !== 'all' ? parseInt(currentWarehouseId, 10) : undefined,
      // Always include hardcoded variants with panel product assignments
      variants: PANEL_VARIANTS.map((v) => ({
        id: v.id,
        label: v.label,
        products: { [PANEL_CATEGORY_ID]: panelVariantProducts[v.id] || [] },
      })),
      categories: kitCategories.map((cat, index) => ({
        category_id: parseInt(cat.category_id),
        is_mandatory: cat.is_mandatory,
        min_qty: parseInt(cat.min_qty) || 1,
        max_qty: parseInt(cat.max_qty) || 1,
        sort_order: index,
        label: parseInt(cat.category_id) === 3 ? (cat.label || '').trim() : null,
        skus: cat.skus.map((sku) => ({ product_id: sku.product_id })),
      })),
    };

    // Always open warehouse selection modal when editing, so changes can be applied to any set of warehouses
    if (isEdit) {
      setPendingPayload(payload);
      await fetchWarehousesForModal();
      setShowWarehouseModal(true);
      return;
    }

    setLoading(true);
    try {
      if (isEdit && kitId) {
        await updateKit(kitId, payload, imageFile);
        toast.success('Kit updated successfully!');
      } else {
        if (!currentWarehouseId || currentWarehouseId === 'all' || payload.warehouse_ids) {
          await createKitBulk(payload, imageFile);
          toast.success('Kit created for all warehouses successfully!');
        } else {
          await createKit(payload, imageFile);
          toast.success('Kit created successfully!');
        }
      }
      navigate('/kits');
    } catch (error) {
      toast.error(error.message || `Failed to ${isEdit ? 'update' : 'create'} kit`);
    } finally {
      setLoading(false);
    }
  };

  const optionalCount = kitCategories.filter((c) => !c.is_mandatory).length;

  if (pageLoading) {
    return (
      <div className="container">
        <div className="card" style={{ textAlign: 'center', padding: '80px 20px' }}>
          <div style={{ display: 'inline-block', width: '40px', height: '40px', border: '4px solid #e9ecef', borderTop: '4px solid #0d6efd', borderRadius: '50%', animation: 'spin 0.8s linear infinite', marginBottom: '16px' }} />
          <p style={{ color: '#6c757d', fontSize: '15px', margin: 0 }}>Loading kit data...</p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  return (
    <div className="container" style={{ position: 'relative' }}>
      {/* Submission Loading Overlay */}
      {loading && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.75)',
            backdropFilter: 'blur(3px)',
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '14px',
          }}
        >
          <div
            style={{
              width: '48px',
              height: '48px',
              border: '4px solid #e9ecef',
              borderTop: '4px solid #198754',
              borderRadius: '50%',
              animation: 'spin 0.8s linear infinite',
            }}
          />
          <div style={{ fontWeight: 600, fontSize: '16px', color: '#212529' }}>
            {isEdit ? 'Updating Custom Kit...' : 'Creating Custom Kit...'}
          </div>
          <div style={{ fontSize: '13px', color: '#6c757d' }}>
            Please wait while we save your kit configuration and upload assets.
          </div>
        </div>
      )}

      <div className="card">
        <div className="order-detail-header">
          <div className="order-detail-title">
            <div className="breadcrumb">
              <Link to="/kits">Custom Kits</Link> / {isEdit ? 'Edit Kit' : 'Create New Kit'}
            </div>
            <h1>{isEdit ? 'Edit Kit' : 'Create New Kit'}</h1>
            {isEdit && (batchWarehouses.length > 1 || warehouseKits.length >= 1) && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
                {batchWarehouses.length > 1 && (
                  <SwitcherDropdown
                    label="Warehouse"
                    value={selectedWarehouseId ? String(selectedWarehouseId) : ''}
                    options={batchWarehouses.map((bw) => ({
                      value: String(bw.warehouseId),
                      label: bw.warehouseName
                        ? `${bw.warehouseName}${bw.warehouseCity ? ` (${bw.warehouseCity})` : ''}`
                        : `Warehouse ${bw.warehouseId}`,
                    }))}
                    onChange={(whId) => handleWarehouseSwitch(whId)}
                    disabled={batchSwitcherLoading || kitSwitcherLoading}
                  />
                )}
                {(warehouseKits.length >= 1 || kitSwitcherLoading) && (
                  <SwitcherDropdown
                    label="Kit"
                    value={String(kitId)}
                    options={warehouseKits.map((k) => ({
                      value: String(k.id),
                      label: k.name,
                    }))}
                    onChange={(selectedKitId) => { if (selectedKitId !== String(kitId)) navigate(`/kits/edit/${selectedKitId}`); }}
                    disabled={kitSwitcherLoading}
                  />
                )}
              </div>
            )}
          </div>
          <div className="order-actions">
            <Link to="/kits" className="btn btn-secondary" style={{ pointerEvents: loading ? 'none' : 'auto', opacity: loading ? 0.6 : 1 }}>Cancel</Link>
            <button
              type="submit"
              form="create-kit-form"
              disabled={loading}
              className="btn btn-success"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
            >
              {loading && (
                <span
                  style={{
                    display: 'inline-block',
                    width: '14px',
                    height: '14px',
                    border: '2px solid rgba(255,255,255,0.4)',
                    borderTop: '2px solid white',
                    borderRadius: '50%',
                    animation: 'spin 0.8s linear infinite',
                  }}
                />
              )}
              {loading ? (isEdit ? 'Updating...' : 'Creating...') : (isEdit ? 'Update Kit' : 'Create Kit')}
            </button>
          </div>
        </div>

        <form id="create-kit-form" onSubmit={handleSubmit}>
          {/* Basic Information */}
          <div className="card">
            <h3 className="section-title">Basic Information</h3>
            <div className="order-info">
              <div className="info-item">
                <label className="info-label">Category *</label>
                <select className="form-control" value={4} disabled>
                  <option value={4}>Kit</option>
                </select>
              </div>
              <div className="info-item">
                <label className="info-label">Kit Name *</label>
                <input
                  type="text"
                  name="name"
                  className="form-control"
                  value={kitData.name}
                  onChange={handleInputChange}
                  placeholder="e.g. ZunSolar 5 kW DCR"
                  required
                />
              </div>
              <div className="info-item">
                <label className="info-label">Kit Discount % *</label>
                <input
                  type="number"
                  name="discount_percent"
                  className="form-control"
                  value={kitData.discount_percent}
                  onChange={handleInputChange}
                  placeholder="e.g. 10"
                  min="0"
                  max="100"
                  step="0.01"
                  required
                />
              </div>
              <div className="info-item">
                <label className="info-label">GST % *</label>
                <input
                  type="number"
                  name="gst_percent"
                  className="form-control"
                  value={kitData.gst_percent}
                  onChange={handleInputChange}
                  placeholder="e.g. 5"
                  min="0"
                  max="28"
                  step="0.01"
                />
              </div>
              <div className="info-item">
                <label className="info-label">Tags *</label>
                <input
                  type="text"
                  name="tags"
                  className="form-control"
                  value={kitData.tags}
                  onChange={handleInputChange}
                  placeholder="Comma separated tags (e.g., solar, panel, 5kw)"
                  required
                />
              </div>
              <div className="info-item" style={{ gridColumn: '1 / -1' }}>
                <label className="info-label">Description *</label>
                <textarea
                  name="description"
                  className="form-control"
                  value={kitData.description}
                  onChange={handleInputChange}
                  placeholder="Short description for kit card display (e.g. Complete solar kit with panels, inverter & accessories)"
                  rows={2}
                  maxLength={200}
                  style={{ resize: 'vertical' }}
                />
                <small style={{ color: '#6c757d', fontSize: '11px' }}>{kitData.description.length}/200 characters</small>
              </div>
              <div className="info-item">
                <label className="info-label">HSN Code *</label>
                <input
                  type="text"
                  name="hsn_code"
                  className="form-control"
                  value={kitData.hsn_code}
                  onChange={handleInputChange}
                  placeholder="e.g. 854140"
                  minLength={6}
                  disabled={isEdit}
                  required
                />
                <small style={{ color: '#6c757d', fontSize: '11px' }}>
                  {isEdit ? 'HSN Code is frozen and cannot be edited' : 'Minimum 6 digits'}
                </small>
              </div>
              <div className="info-item">
                <label className="info-label">Min Optional Categories</label>
                <input
                  type="number"
                  name="min_optional_count"
                  className="form-control"
                  value={kitData.min_optional_count}
                  onChange={handleInputChange}
                  placeholder="0"
                  min="0"
                  max={optionalCount || 99}
                />
                <small style={{ color: '#6c757d', fontSize: '11px' }}>
                  Customer must select at least this many optional categories
                </small>
              </div>
            </div>
          </div>

          {/* Kit Image */}
          <div className="card">
            <h3 className="section-title">Kit Image</h3>
            <div className="order-info">
              <div className="info-item">
                <label className="info-label">Upload Image *</label>
                <input
                  type="file"
                  className="form-control"
                  accept="image/*"
                  onChange={handleImageChange}
                />
                <small style={{ color: '#6c757d', fontSize: '11px' }}>Max 10MB. JPEG, PNG supported.</small>
              </div>
              {imagePreview && imagePreview.startsWith('http') || imagePreview && imagePreview.startsWith('blob') ? (
                <div className="info-item">
                  <label className="info-label">Preview</label>
                  <img
                    src={imagePreview}
                    alt="Kit"
                    style={{ width: '120px', height: '120px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #dee2e6' }}
                  />
                </div>
              ) : null}
            </div>
          </div>

          {/* Panel Variants — hardcoded names, admin assigns panel SKUs */}
          {kitCategories.some((c) => parseInt(c.category_id) === PANEL_CATEGORY_ID && c.skus.length > 0) && (
            <div className="card">
              <h3 className="section-title" style={{ marginBottom: '4px' }}>Panel Variants</h3>
              <small style={{ color: '#6c757d', display: 'block', marginBottom: '14px' }}>
                Assign each Solar Panel SKU to a variant. Inverters and BOS are not affected.
              </small>
              {PANEL_VARIANTS.map((v) => {
                const panelCat = kitCategories.find((c) => parseInt(c.category_id) === PANEL_CATEGORY_ID);
                const assignedIds = panelVariantProducts[v.id] || [];
                const searchTerm = (variantSearch[v.id] || '').toLowerCase().trim();
                const filteredSkus = (panelCat?.skus || []).filter((sku) => {
                  if (!searchTerm) return true;
                  const name = (sku.product_name || '').toLowerCase();
                  const code = (sku.sku_code || '').toLowerCase();
                  return name.includes(searchTerm) || code.includes(searchTerm);
                });

                return (
                  <div key={v.id} style={{ marginBottom: '12px', padding: '12px', border: '1px solid #e1bee7', borderRadius: '6px', background: '#faf5ff' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px', marginBottom: '10px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontWeight: 600, fontSize: '14px', color: '#6a1b9a' }}>{v.label}</span>
                        <span style={{ fontSize: '11px', color: '#6c757d' }}>({assignedIds.length} selected)</span>
                        <button type="button" onClick={() => selectAllPanelVariantProducts(v.id, searchTerm ? filteredSkus : null)} style={{ fontSize: isMobile ? '13px' : '10px', padding: isMobile ? '8px 14px' : '2px 8px', background: '#e9ecef', border: '1px solid #ced4da', borderRadius: '3px', cursor: 'pointer' }}>All</button>
                        <button type="button" onClick={() => clearPanelVariantProducts(v.id, searchTerm ? filteredSkus : null)} style={{ fontSize: isMobile ? '13px' : '10px', padding: isMobile ? '8px 14px' : '2px 8px', background: '#e9ecef', border: '1px solid #ced4da', borderRadius: '3px', cursor: 'pointer' }}>Clear</button>
                      </div>
                      <input
                        type="text"
                        className="form-control"
                        placeholder={`Search ${v.label} products...`}
                        value={variantSearch[v.id] || ''}
                        onChange={(e) => setVariantSearch({ ...variantSearch, [v.id]: e.target.value })}
                        style={{ fontSize: '12px', padding: '4px 10px', height: '30px', width: '240px', borderRadius: '4px', border: '1px solid #ced4da' }}
                      />
                    </div>
                    {filteredSkus.length === 0 ? (
                      <div style={{ fontSize: '12px', color: '#6c757d', fontStyle: 'italic', padding: '4px 0' }}>
                        No products match "{variantSearch[v.id]}"
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {filteredSkus.map((sku) => {
                          const isSelected = assignedIds.includes(sku.product_id);
                          return (
                            <div
                              key={sku.product_id}
                              onClick={() => togglePanelVariantProduct(v.id, sku.product_id)}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                padding: '4px 10px',
                                fontSize: '12px',
                                borderRadius: '14px',
                                cursor: 'pointer',
                                border: isSelected ? '1px solid #7b1fa2' : '1px solid #dee2e6',
                                background: isSelected ? '#e1bee7' : '#f8f9fa',
                                color: isSelected ? '#4a148c' : '#6c757d',
                                userSelect: 'none',
                              }}
                            >
                              {isSelected && <span>✓</span>}
                              <span>{sku.product_name || `#${sku.product_id}`}{isSelected && sku.sku_code ? <span style={{ fontFamily: 'monospace', fontSize: '10px', opacity: 0.75 }}> ({sku.sku_code})</span> : ''}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Kit Categories */}
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap' }}>
              <h3 className="section-title" style={{ marginBottom: '0' }}>Kit Categories & Eligible SKUs</h3>
              <button type="button" onClick={addCategory} className="btn btn-primary" style={{ fontSize: '12px', padding: '8px 16px' }}>
                + Add Category
              </button>
            </div>

            {productsLoading && (
              <div style={{ padding: '16px', textAlign: 'center', color: '#0d6efd', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                <div style={{ width: '18px', height: '18px', border: '2px solid #b6d4fe', borderTop: '2px solid #0d6efd', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                <span style={{ fontSize: '13px', fontWeight: 500 }}>Loading product catalog...</span>
              </div>
            )}

            {kitCategories.length === 0 && !productsLoading && (
              <div style={{ padding: '24px', textAlign: 'center', color: '#6c757d', background: '#f8f9fa', borderRadius: '6px' }}>
                No categories added yet. Click "+ Add Category" to start building the kit.
              </div>
            )}

            {kitCategories.map((cat, catIndex) => (
              <div key={catIndex} style={{ marginBottom: '15px', padding: '15px', border: '1px solid #e9ecef', borderRadius: '6px', background: cat.is_mandatory ? '#f8fbff' : '#fffdf5' }}>
                {/* Category header — collapsible */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }} onClick={() => toggleCategoryCollapse(catIndex)}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '14px', transition: 'transform 0.2s', transform: collapsedCategories[catIndex] ? 'rotate(-90deg)' : 'rotate(0deg)' }}>▼</span>
                    <span style={{ fontWeight: 600, fontSize: '14px' }}>
                      {categories.find((c) => c.id === parseInt(cat.category_id))?.name || `Category ${catIndex + 1}`}
                    </span>
                    <span style={{ fontSize: '11px', color: cat.is_mandatory ? '#1565c0' : '#e65100', background: cat.is_mandatory ? '#e3f2fd' : '#fff3e0', padding: '2px 8px', borderRadius: '8px' }}>
                      {cat.is_mandatory ? 'Mandatory' : 'Optional'}
                    </span>
                    {cat.skus.length > 0 && <span style={{ fontSize: '11px', color: '#6c757d' }}>{cat.skus.length} SKUs</span>}
                  </div>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); removeCategory(catIndex); }}
                    className="btn btn-secondary"
                    style={{ fontSize: '11px', padding: '4px 10px', backgroundColor: '#dc3545', borderColor: '#dc3545', color: 'white' }}
                  >
                    Remove
                  </button>
                </div>

                {!collapsedCategories[catIndex] && (
                  <div style={{ marginTop: '12px' }}>
                {/* Category row config */}
                <div className="order-info" style={{ marginBottom: '12px' }}>
                  <div className="info-item">
                    <label className="info-label">Category *</label>
                    <select
                      className="form-control"
                      value={cat.category_id}
                      onChange={(e) => updateCategory(catIndex, 'category_id', e.target.value)}
                    >
                      <option value="">Select category</option>
                      {categories.filter((c) => c.id !== 4).map((c) => (
                        <option
                          key={c.id}
                          value={c.id}
                          disabled={SINGLE_INSTANCE_CATEGORIES.includes(c.id) && getUsedCategoryIds().includes(c.id) && parseInt(cat.category_id) !== c.id}
                        >
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="info-item">
                    <label className="info-label">Type</label>
                    <select
                      className="form-control"
                      value={cat.is_mandatory ? 'mandatory' : 'optional'}
                      onChange={(e) => updateCategory(catIndex, 'is_mandatory', e.target.value === 'mandatory')}
                    >
                      <option value="mandatory">Mandatory</option>
                      <option value="optional">Optional</option>
                    </select>
                  </div>
                  <div className="info-item">
                    <label className="info-label">Min Qty</label>
                    <input
                      type="number"
                      className="form-control"
                      value={cat.min_qty}
                      onChange={(e) => updateCategory(catIndex, 'min_qty', e.target.value)}
                      min="1"
                    />
                  </div>
                  <div className="info-item">
                    <label className="info-label">Max Qty</label>
                    <input
                      type="number"
                      className="form-control"
                      value={cat.max_qty}
                      onChange={(e) => updateCategory(catIndex, 'max_qty', e.target.value)}
                      min="1"
                    />
                  </div>
                  {parseInt(cat.category_id) === 3 && (
                    <div className="info-item">
                      <label className="info-label">Label *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={cat.label || ''}
                        onChange={(e) => updateCategory(catIndex, 'label', e.target.value)}
                        placeholder="e.g. ACDB, DCDB, Earthing Kit"
                        maxLength={100}
                        required
                      />
                      <small style={{ color: '#6c757d', fontSize: '11px' }}>Required label for BOS items</small>
                    </div>
                  )}
                </div>

                {/* SKU selection */}
                {cat.category_id && (
                  <div style={{ borderTop: '1px solid #e9ecef', paddingTop: '10px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap', gap: '6px' }}>
                      <label className="info-label" style={{ marginBottom: 0, fontSize: '12px' }}>
                        Eligible SKUs ({cat.skus.length} selected)
                      </label>
                      <button type="button" onClick={() => selectAllSkus(catIndex)} className="btn btn-secondary" style={{ fontSize: isMobile ? '13px' : '11px', padding: isMobile ? '8px 14px' : '4px 10px' }}>
                        Select All
                      </button>
                    </div>

                    <div style={{ position: 'relative', marginBottom: '8px' }}>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="+ Search & add a product..."
                        value={skuSearch[catIndex] !== undefined ? (skuSearch[catIndex] || '') : ''}
                        onChange={(e) => setSkuSearch({ ...skuSearch, [catIndex]: e.target.value })}
                        onFocus={() => setSkuSearch((prev) => ({ ...prev, [catIndex]: prev[catIndex] || '' }))}
                        onBlur={() => setTimeout(() => setSkuSearch((prev) => ({ ...prev, [catIndex]: undefined })), 150)}
                      />
                      {skuSearch[catIndex] !== undefined && (() => {
                        const search = (skuSearch[catIndex] || '').toLowerCase();
                        const filtered = getAvailableProductsForCategory(catIndex)
                          .filter((p) => !cat.skus.find((s) => s.product_id === p.pid))
                          .filter((p) => !search || p.productName.toLowerCase().includes(search) || (p.skuId || '').toLowerCase().includes(search));

                        return (
                          <div
                            onMouseDown={(e) => e.preventDefault()}
                            style={{
                              position: 'absolute',
                              top: '100%',
                              left: 0,
                              right: 0,
                              zIndex: 10,
                              background: 'white',
                              border: '1px solid #ced4da',
                              borderTop: 'none',
                              borderRadius: '0 0 4px 4px',
                              maxHeight: '200px',
                              overflowY: 'auto',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            }}
                          >
                            {filtered.length > 0 ? (
                              filtered.slice(0, 40).map((p) => (
                                <div
                                  key={p.pid}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    addSku(catIndex, p.pid);
                                  }}
                                  style={{
                                    padding: '8px 12px',
                                    fontSize: '12px',
                                    cursor: 'pointer',
                                    borderBottom: '1px solid #f0f0f0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                  }}
                                  onMouseOver={(e) => (e.currentTarget.style.background = '#f0f7ff')}
                                  onMouseOut={(e) => (e.currentTarget.style.background = 'white')}
                                >
                                  <span>{p.productName} <span style={{ color: '#6c757d', fontSize: '11px' }}>({p.skuId})</span></span>
                                  <span style={{ fontSize: '11px', color: '#0d6efd', fontWeight: 600 }}>+ Add</span>
                                </div>
                              ))
                            ) : (
                              <div style={{ padding: '10px 12px', fontSize: '12px', color: '#6c757d', fontStyle: 'italic' }}>
                                {search ? `No products match "${skuSearch[catIndex]}"` : 'All eligible products selected'}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>

                    {/* Chips */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {cat.skus.map((sku, skuIndex) => (
                        <div
                          key={sku.product_id}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '5px',
                            background: '#e9ecef',
                            borderRadius: '16px',
                            padding: '4px 10px',
                            fontSize: '12px',
                          }}
                        >
                          <span style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                            <span>{sku.product_name}</span>
                            {sku.sku_code && <span style={{ fontSize: '10px', color: '#6c757d', fontFamily: 'monospace' }}>{sku.sku_code}</span>}
                          </span>
                          <button
                            type="button"
                            onClick={() => removeSku(catIndex, skuIndex)}
                            style={{ background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px', lineHeight: 1 }}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                  </div>
                )}
              </div>
            ))}
          </div>
          {isMobile && (
            <div className="m-sticky-actions">
              <Link to="/kits" className="btn btn-secondary" style={{ pointerEvents: loading ? 'none' : 'auto', opacity: loading ? 0.6 : 1 }}>Cancel</Link>
              <button type="submit" disabled={loading} className="btn btn-success">
                {loading ? (isEdit ? 'Updating...' : 'Creating...') : (isEdit ? 'Update Kit' : 'Create Kit')}
              </button>
            </div>
          )}
        </form>
      </div>

      {/* Warehouse Selection Modal for Bulk Update */}
      {showWarehouseModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.65)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
          }}
        >
          <div
            style={{
              background: '#ffffff',
              borderRadius: '12px',
              maxWidth: '780px',
              width: '92%',
              maxHeight: '85vh',
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Modal Header */}
            <div style={{ padding: '20px 28px', borderBottom: '1px solid #e9ecef', background: '#f8f9fa' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#1e293b' }}>
                  Select Target Warehouses
                </h3>
                <span style={{ fontSize: '13px', fontWeight: 600, color: '#0d6efd', background: '#e7f1ff', padding: '4px 12px', borderRadius: '12px' }}>
                  {selectedWarehouseIds.length} of {selectableWarehouses.length} Selected
                </span>
              </div>
              <p style={{ margin: '6px 0 0 0', fontSize: '13px', color: '#64748b' }}>
                Choose the warehouses where you want to apply these custom kit configuration updates.
              </p>
            </div>

            {/* Modal Body */}
            <div
              style={{
                padding: '24px 28px',
                maxHeight: '480px',
                overflowY: 'auto',
                WebkitOverflowScrolling: 'touch',
                overscrollBehavior: 'contain',
              }}
            >
              {warehousesLoading ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#0d6efd', fontWeight: 500, fontSize: '15px' }}>
                  <div style={{ display: 'inline-block', width: '28px', height: '28px', border: '3px solid #b6d4fe', borderTop: '3px solid #0d6efd', borderRadius: '50%', animation: 'spin 0.8s linear infinite', marginBottom: '10px' }} />
                  <div>Loading warehouses list...</div>
                </div>
              ) : (
                <>
                  {/* Select All Row */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '12px 16px',
                      background: '#f1f5f9',
                      borderRadius: '8px',
                      marginBottom: '16px',
                      border: '1px solid #e2e8f0',
                    }}
                  >
                    <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontWeight: 600, fontSize: '14px', margin: 0, color: '#0f172a' }}>
                      <input
                        type="checkbox"
                        checked={selectableWarehouses.length > 0 && selectedWarehouseIds.length === selectableWarehouses.length}
                        onChange={handleToggleSelectAllWarehouses}
                        style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: '#0d6efd' }}
                      />
                      Select All Batch Warehouses
                    </label>
                    <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 500 }}>
                      Only hubs in this batch are editable
                    </span>
                  </div>

                  {/* Warehouses Checkbox Grid */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: '10px' }}>
                    {allWarehousesList.map((wh) => {
                      const isBatchHub = batchWarehouseIds.length === 0 || batchWarehouseIds.includes(wh.id);
                      const isChecked = selectedWarehouseIds.includes(wh.id);
                      return (
                        <label
                          key={wh.id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '10px 14px',
                            border: isChecked ? '1px solid #0d6efd' : '1px solid #e2e8f0',
                            background: isChecked ? '#eff6ff' : '#ffffff',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            opacity: 1,
                            fontSize: '14px',
                            userSelect: 'none',
                          }}
                          title={!isBatchHub ? 'Click to add this newly created hub to this kit batch' : ''}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => handleToggleSingleWarehouse(wh.id)}
                            style={{ width: '17px', height: '17px', cursor: 'pointer', accentColor: '#0d6efd' }}
                          />
                          <span style={{ fontWeight: isChecked ? 600 : 500, color: '#1e293b', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', width: '100%', justifyContent: 'space-between' }}>
                            <span>
                              {wh.name} <span style={{ color: '#64748b', fontSize: '12px' }}>({wh.city})</span>
                              {(wh.hub_type === 'VIRTUAL' || wh.hubType === 'VIRTUAL') && (
                                <span style={{ display: 'block', color: '#64748b', fontSize: '11px', fontWeight: 400 }}>
                                  (Virtual Hub)
                                </span>
                              )}
                            </span>
                            {isBatchHub && (
                              <span style={{ fontSize: '10px', background: '#d1e7dd', color: '#0f5132', padding: '2px 8px', borderRadius: '10px', fontWeight: 600 }}>
                                Batched Hub
                              </span>
                            )}
                            {!isBatchHub && (
                              <span style={{ fontSize: '10px', background: '#e0f2fe', color: '#0369a1', padding: '2px 8px', borderRadius: '10px', fontWeight: 500 }}>
                                + Add to Batch
                              </span>
                            )}
                          </span>
                        </label>
                      );
                    })}
                  </div>

                  {selectedWarehouseIds.length === 0 && (
                    <div style={{ color: '#dc3545', fontSize: '13px', marginTop: '16px', fontWeight: 600, textAlign: 'center', background: '#fff5f5', padding: '10px', borderRadius: '6px' }}>
                      ⚠️ Please select at least one warehouse to update.
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Modal Footer */}
            <div
              style={{
                padding: '16px 28px',
                borderTop: '1px solid #e9ecef',
                background: '#f8f9fa',
                display: 'flex',
                justify: 'flex-end',
                gap: '12px',
              }}
            >
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowWarehouseModal(false)}
                style={{ fontSize: '14px', padding: '8px 20px' }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-success"
                disabled={selectedWarehouseIds.length === 0 || loading}
                onClick={handleBulkUpdateSubmit}
                style={{ fontSize: '14px', padding: '8px 24px', fontWeight: 600 }}
              >
                Confirm & Update Kit ({selectedWarehouseIds.length} Hubs)
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateKit;
